/**
 * DevUserSwitcher — shown instead of login when DEV_AUTH_BYPASS=true.
 * Lets you pick any user in the database and sign in instantly.
 */

import React, { useEffect, useState } from 'react';
import { fetchDevUsers } from '../../agents/authAgent';
import './AuthPage.css';

const ROLE_ICONS = { buyer: '🏠', seller: '🏷️', agent: '🤝' };

export default function DevUserSwitcher({ onSwitch, error }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchDevUsers();
        if (!cancelled) setUsers(data);
      } catch {
        /* bypass not available */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = filter === 'all' ? users : users.filter(u => u.role === filter);
  const roles = [...new Set(users.map(u => u.role))];

  return (
    <div className="auth-page">
      <div className="auth-card auth-card--wide">
        <div className="auth-brand">
          <span className="auth-brand-icon">🔓</span>
          <span className="auth-brand-name">Dev Mode — Pick a User</span>
        </div>

        <p style={{ color: 'var(--color-text-muted, #7C7A95)', marginBottom: 16, fontSize: 14 }}>
          Auth bypass is active. Click any user to sign in instantly.
        </p>

        {error && (
          <div className="auth-error" role="alert">{error}</div>
        )}

        {/* Role filter */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} label="All" />
          {roles.map(r => (
            <FilterPill key={r} active={filter === r} onClick={() => setFilter(r)}
              label={`${ROLE_ICONS[r] || '👤'} ${r.charAt(0).toUpperCase() + r.slice(1)}`} />
          ))}
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 24, color: '#888' }}>Loading users…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
            {filtered.map(u => (
              <button
                key={u.id}
                onClick={() => onSwitch(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', border: '1px solid var(--color-border, #E6E4F0)',
                  borderRadius: 10, background: 'var(--color-bg-content, #fff)',
                  cursor: 'pointer', textAlign: 'left', transition: 'background .15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-page, #FBF8F7)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--color-bg-content, #fff)'}
              >
                <span style={{ fontSize: 22 }}>{ROLE_ICONS[u.role] || '👤'}</span>
                <span style={{ flex: 1 }}>
                  <strong style={{ color: 'var(--color-text-primary, #1E1B3A)' }}>
                    {u.first_name} {u.last_name}
                  </strong>
                  <br />
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted, #7C7A95)' }}>
                    {u.email} · {u.role}
                  </span>
                </span>
                <span style={{ fontSize: 12, color: '#aaa' }}>id {u.id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
      border: active ? '1.5px solid var(--color-brand, #6C5CE7)' : '1px solid var(--color-border, #E6E4F0)',
      background: active ? 'var(--color-brand, #6C5CE7)' : 'transparent',
      color: active ? '#fff' : 'var(--color-text-secondary, #5A5873)',
      fontWeight: active ? 600 : 400,
    }}>
      {label}
    </button>
  );
}
